import Alpine from 'alpinejs';
import collapse from '@alpinejs/collapse';

Alpine.plugin(collapse);

Alpine.data('ttswpAudioPlayer', (slug) => ({
    audio: null,
    playing: false,
    currentTime: 0,
    duration: 0,
    speed: 1,
    stickyVisible: false,
    stickyDismissed: false,
    inlineVisible: true,

    init() {
        this.audio = this.$refs.audio;
        this.stickyDismissed = sessionStorage.getItem('ttswp.audio.dismissed.' + slug) === '1';

        this.audio.addEventListener('loadedmetadata', () => {
            this.duration = this.audio.duration || 0;
        });

        let last = 0;
        this.audio.addEventListener('timeupdate', () => {
            const now = performance.now();
            if (now - last < 250) return;
            last = now;
            this.currentTime = this.audio.currentTime;
        });

        this.audio.addEventListener('ended', () => {
            this.playing = false;
            this.stickyVisible = false;
        });

        this.audio.addEventListener('pause', () => {
            this.playing = false;
            this.stickyVisible = false;
        });

        if (window.matchMedia('(max-width: 767px)').matches) {
            const observer = new IntersectionObserver(
                ([entry]) => { this.inlineVisible = entry.isIntersecting; this.evalSticky(); },
                { rootMargin: '-100px 0px 0px 0px', threshold: 0 },
            );
            observer.observe(this.$refs.inline);
        }
    },

    toggle() {
        if (this.audio.paused) {
            this.audio.play();
            this.playing = true;
            this.evalSticky();
        } else {
            this.audio.pause();
        }
    },

    seek(event) {
        const v = Number(event.target.value);
        this.audio.currentTime = v;
        this.currentTime = v;
    },

    cycleSpeed() {
        const speeds = [1, 1.25, 1.5];
        this.speed = speeds[(speeds.indexOf(this.speed) + 1) % speeds.length];
        this.audio.playbackRate = this.speed;
    },

    evalSticky() {
        this.stickyVisible = this.playing
            && !this.inlineVisible
            && !this.stickyDismissed
            && window.matchMedia('(max-width: 767px)').matches;
    },

    dismissSticky() {
        this.stickyDismissed = true;
        this.stickyVisible = false;
        sessionStorage.setItem('ttswp.audio.dismissed.' + slug, '1');
    },

    fmt(seconds) {
        if (!seconds || isNaN(seconds)) return '0:00';
        const m = Math.floor(seconds / 60);
        const s = Math.floor(seconds % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    },
}));

window.Alpine = Alpine;

// Inline "Listen" buttons on blog listing cards (post-card, featured-card). One
// shared <audio> element per page means only one track can ever play at a time -
// one-at-a-time is structural, not bookkeeping. Progressive enhancement: the
// delegated listener simply never fires on pages without a [data-tts-listen]
// button, so this is a no-op everywhere else.
function initBlogListen() {
    let audio = null;
    let activeBtn = null;

    const setState = (btn, playing) => {
        if (!btn) return;
        btn.setAttribute('aria-pressed', playing ? 'true' : 'false');
        const play = btn.querySelector('[data-tts-icon="play"]');
        const pause = btn.querySelector('[data-tts-icon="pause"]');
        if (play) play.classList.toggle('hidden', playing);
        if (pause) pause.classList.toggle('hidden', !playing);
    };

    document.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-tts-listen]');
        if (!btn) return;
        event.preventDefault();

        const url = btn.getAttribute('data-audio-url');
        if (!url) return;

        if (!audio) {
            audio = new Audio();
            audio.preload = 'none';
            // Drive button visuals off the element's own events so they stay in
            // sync regardless of how playback started or stopped.
            audio.addEventListener('play', () => setState(activeBtn, true));
            audio.addEventListener('pause', () => setState(activeBtn, false));
            audio.addEventListener('ended', () => { setState(activeBtn, false); activeBtn = null; });
        }

        // Same button: toggle play/pause of the current track.
        if (activeBtn === btn) {
            if (audio.paused) audio.play(); else audio.pause();
            return;
        }

        // Different button: reset the previous one and start the new track.
        setState(activeBtn, false);
        activeBtn = btn;
        audio.src = url;
        audio.currentTime = 0;
        audio.play();
    });
}

initBlogListen();

// The hero TTS demo only exists on the homepage. Lazy-load its module (a
// separate Vite chunk) and register the component before Alpine.start() so the
// ~330 lines of Web Audio code never ship on pages without the demo.
(async () => {
    if (document.querySelector('[x-data*="ttswpHeroDemo"]')) {
        const { default: registerHeroDemo } = await import('./hero-demo');
        registerHeroDemo(Alpine);
    }
    Alpine.start();
})();
